import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, MapPin, MessageCircle, ShoppingBag } from "lucide-react";
import {
  WHATSAPP_NUMBER,
  WHATSAPP_DISPLAY,
  STORE_HOURS_WEEK,
  STORE_HOURS_SUNDAY,
  formatBRL,
} from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "novo", label: "Pedido recebido", desc: "A loja recebeu seu pedido" },
  { key: "em_preparo", label: "Em preparo", desc: "Seu pedido está sendo montado com carinho" },
  { key: "saiu_entrega", label: "Saiu pra entrega", desc: "O entregador está a caminho" },
  { key: "entregue", label: "Entregue", desc: "Aproveite!" },
];

const stepIndex = (status) => Math.max(0, STEPS.findIndex((s) => s.key === status));

export const OrderTracking = ({ orderId }) => {
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/orders/${orderId}`);
      if (!r.ok) throw new Error();
      setOrder(await r.json());
      setUpdatedAt(new Date());
    } catch {
      setError(true);
    }
  }, [orderId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  const current = order ? stepIndex(order.status) : 0;

  return (
    <div className="min-h-screen bg-[#1E053B] text-white antialiased" data-testid="tracking-page">
      <header className="border-b border-purple-500/20 px-4 py-5">
        <div className="mx-auto flex max-w-md items-center justify-between">
          <a href="/" className="font-syne text-xl font-extrabold tracking-tight text-white">
            GIGA<span className="text-[#A855F7]">AÇAÍ</span>
          </a>
          <span className="font-grotesk text-[10px] font-bold uppercase tracking-[0.25em] text-purple-300/60">
            Acompanhar pedido
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-md px-4 py-10">
        {!order && !error && (
          <div className="flex flex-col items-center gap-4 py-24">
            <Loader2 className="h-9 w-9 animate-spin text-amber-400" />
            <p className="text-sm text-purple-200/70">Buscando seu pedido...</p>
          </div>
        )}

        {error && (
          <div className="rounded-[2rem] border border-purple-500/25 bg-[#2D0B5A]/60 p-10 text-center" data-testid="tracking-not-found">
            <p className="font-syne text-xl font-extrabold text-white">Pedido não encontrado</p>
            <p className="mt-3 text-sm text-purple-200/70">
              Confira se o link está completo ou chame a loja no WhatsApp.
            </p>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 font-grotesk text-xs font-bold uppercase tracking-[0.12em] text-[#1E053B]"
            >
              <MessageCircle className="h-4 w-4" /> Falar com a loja
            </a>
          </div>
        )}

        {order && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="rounded-[2rem] border border-amber-400/30 bg-[#2D0B5A]/70 p-6 text-center backdrop-blur-xl">
              <p className="font-grotesk text-[10px] font-bold uppercase tracking-[0.3em] text-amber-400">
                Status agora
              </p>
              <h1
                data-testid="tracking-status-label"
                className="mt-2 font-syne text-3xl font-extrabold text-white"
              >
                {STEPS[current].label}
              </h1>
              <p className="mt-1.5 text-sm text-purple-200/75">{STEPS[current].desc}</p>
              {updatedAt && (
                <p className="mt-3 text-[11px] text-purple-300/50">
                  Atualiza sozinho a cada 20s · última verificação{" "}
                  {updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>

            <div className="mt-8 rounded-[2rem] border border-purple-500/25 bg-[#2D0B5A]/60 p-7">
              <div className="space-y-0">
                {STEPS.map((s, i) => {
                  const done = i < current;
                  const active = i === current;
                  return (
                    <div key={s.key} className="flex gap-4" data-testid={`tracking-step-${i}`}>
                      <div className="flex flex-col items-center">
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-500 ${
                            done
                              ? "border-emerald-400 bg-emerald-400 text-[#1E053B]"
                              : active
                                ? "border-amber-400 bg-amber-400/15 text-amber-300"
                                : "border-purple-500/30 bg-transparent text-purple-400/40"
                          }`}
                        >
                          {done ? (
                            <Check className="h-4.5 w-4.5" strokeWidth={3} />
                          ) : active ? (
                            <span className="relative flex h-2.5 w-2.5">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" />
                              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
                            </span>
                          ) : (
                            <span className="h-2.5 w-2.5 rounded-full bg-purple-500/30" />
                          )}
                        </span>
                        {i < STEPS.length - 1 && (
                          <span
                            className={`my-1 w-0.5 flex-1 transition-colors duration-500 ${
                              done ? "bg-emerald-400/60" : "bg-purple-500/20"
                            }`}
                            style={{ minHeight: "28px" }}
                          />
                        )}
                      </div>
                      <div className="pb-7">
                        <p
                          className={`text-sm font-bold ${
                            done ? "text-emerald-300" : active ? "text-amber-300" : "text-purple-300/50"
                          }`}
                        >
                          {s.label}
                        </p>
                        <p className={`mt-0.5 text-xs ${active ? "text-purple-200/75" : "text-purple-300/45"}`}>
                          {s.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 rounded-[2rem] border border-purple-500/25 bg-[#2D0B5A]/60 p-7">
              <h2 className="flex items-center gap-2 font-syne text-base font-extrabold text-white">
                <ShoppingBag className="h-4.5 w-4.5 text-amber-400" /> Seu pedido
              </h2>
              <div className="mt-4 space-y-2">
                {order.items.map((it, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-purple-100">
                      {it.qty > 1 ? `${it.qty}x ` : ""}
                      {it.title}
                      {it.details && it.details.length > 0 && (
                        <span className="block text-xs text-purple-300/60">{it.details.join(", ")}</span>
                      )}
                    </span>
                    <span className="shrink-0 font-semibold text-purple-200/80">
                      {formatBRL(it.unitPrice * it.qty)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-1.5 border-t border-purple-500/20 pt-4 text-sm">
                <div className="flex justify-between text-purple-200/70">
                  <span>Subtotal</span>
                  <span>{formatBRL(order.subtotal)}</span>
                </div>
                {order.mode === "entrega" && (
                  <div className="flex justify-between text-purple-200/70">
                    <span>Entrega</span>
                    <span>{order.fee === 0 ? "Grátis" : formatBRL(order.fee)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1">
                  <span className="font-grotesk text-xs font-bold uppercase tracking-[0.2em] text-purple-300/70">
                    Total
                  </span>
                  <span className="font-syne text-xl font-extrabold text-emerald-400">
                    {formatBRL(order.total)}
                  </span>
                </div>
              </div>
              <p className="mt-4 flex items-start gap-2 text-xs text-purple-200/70">
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                {order.mode === "entrega"
                  ? `Entrega em: ${order.address}`
                  : "Retirada no balcão da loja"}
              </p>
              {order.spoons > 0 && (
                <p className="mt-1.5 text-xs text-purple-300/60">
                  {order.spoons} colher{order.spoons > 1 ? "es" : ""} inclusa{order.spoons > 1 ? "s" : ""}
                </p>
              )}
            </div>

            <div className="mt-6 rounded-[2rem] border border-purple-500/25 bg-[#2D0B5A]/60 p-7 text-center">
              <p className="text-sm text-purple-200/80">Alguma dúvida sobre o pedido?</p>
              <a
                data-testid="tracking-whatsapp-link"
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Oi! Sobre meu pedido ${orderId}...`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-7 py-3.5 font-grotesk text-xs font-bold uppercase tracking-[0.12em] text-[#1E053B] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                <MessageCircle className="h-4 w-4" /> Chamar a loja · {WHATSAPP_DISPLAY}
              </a>
              <p className="mt-4 text-[11px] text-purple-300/50">
                {STORE_HOURS_WEEK} · {STORE_HOURS_SUNDAY}
              </p>
            </div>

            <p className="mt-8 text-center">
              <a
                href="/"
                className="text-xs font-semibold text-purple-300/60 underline-offset-4 transition-colors hover:text-amber-300 hover:underline"
              >
                Voltar para o cardápio
              </a>
            </p>
          </motion.div>
        )}
      </main>
    </div>
  );
};
